import React, { useEffect, useState } from 'react';
import moment from 'moment';
import BottomSheetOrDialog from '../../../../components/BottomSheetOrDialog/BottomSheetOrDialog';
import {
  Bot,
  Briefcase,
  Calculator,
  ChevronRight,
  CircleHelp,
  Dumbbell,
  Frown,
  Handshake,
  Info,
  Lightbulb,
  Plus,
  QrCode,
  Route,
  Speech,
  TriangleAlert,
  User,
  Wallet
} from 'lucide-react';
import { useRecruitProfile } from '../recruitProfile';
import type { RecruitProfileKind } from '../recruitProfile';
import RecruitProfileChips, { recruitProfileLabelId } from './RecruitProfileChips';
import { recruitApproachId } from './recruitPlaybook';
import { discLabel, isDiscLetter } from '../../../../constants/disc';
import { COVER_ANIMALS } from '../../../CoverQuiz/coverQuizData';
import DiscTips from '../../../../components/ClientEntry/SmartIdeaDialog/DiscTips';
import {
  DiscLine,
  IdeaCard,
  IdeaEditButton,
  IdeaMeetingFlow,
  IdeaPoints
} from '../../../../components/ClientEntry/SmartIdeaDialog/IdeaKit';
import { RECRUIT_POINT_ORDER, discOrderedPoints } from '../../../../components/ClientEntry/SmartIdeaDialog/discPoints';
import DobReadings from '../../../../components/ClientEntry/SmartIdeaDialog/DobReadings';
import DISCDialogComponent from '../../../../components/ClientEntry/DISCDialog/DISCDialogComponent/DISCDialogComponent';
import SetBirthdayPicker from '../../../../components/ClientEntry/SmartIdeaDialog/SetBirthdayPicker';
import type { IntlLike } from '../clientPresentation';
import { useRecruitScores } from '../recruitScore';
import RecruitScoreSummary from './RecruitScoreSummary';
import RecruitQuizCard from './RecruitQuizCard';
import { RecruitMascot, RecruitOpener, RecruitPersonaCard, TypewriterText } from './RecruitFun';
import { recruitIdeaPathCopy, useRecruitIdeaPath } from './RecruitIdeaPath';
import { useRecruitIdeaWho } from './recruitIdeaWho';
import type { RecruitIdeaWho } from './recruitIdeaWho';
import DiscChemistryCard from '../../../../components/ClientEntry/SmartIdeaDialog/DiscChemistryCard';
import IdeaTabs from '../../../../components/ClientEntry/SmartIdeaDialog/IdeaTabs';

/**
 * 招募 IDEA — the page an adviser reads in the three minutes before a career
 * conversation. Design record:
 * docs/specs/2026-08-31-clientv2-recruitment-mode-design.md §7 (slice 4),
 * screens: docs/specs/2026-09-02-clientv2-recruit-idea-ui-mock.html.
 *
 * Its own file, NOT a tab inside Smart IDEA 🔒 — that component is ~1000 lines
 * bound to policies and coverage, which a candidate usually has none of; it is
 * PRO-gated on terms recruitment has not agreed to; and the two are opened from
 * different views, so sharing a container buys no navigational benefit. The
 * layout is reused, the file is not.
 *
 * Not PRO-gated, for the same reason §4.7 refused to gate the DISC playbook:
 * the conversation advice is a table lookup, nothing is generated and no
 * tokens are spent. The 🤖 section reads a note the adviser already paid to
 * have written; it does not call anything.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * ⚠️ PART FRONT-END ONLY. `DEFAULTS` supplies defaults where the factsheet has no
 * recruitment fields. The five sampled fields in 他是誰 can now be edited locally
 * for this candidate and route; the 🤖 note remains a fixed sample.
 *
 * 痛點 / 顧慮 / 優勢 are no longer sampled: the recruiter picks them as chips on
 * the cards themselves or in 快速資料 (RecruitProfileChips), and an unpicked
 * card says so. Those picks, like 候選人評估, are saved on the factsheet
 * (../recruitProfile).
 *
 * FOUR THINGS ARE REAL, and must stay real: the candidate's NAME, their DISC
 * LETTER, their BIRTH DATE, and their 現時月入. DISC is the field §6.3 shipped in Phase 1 for, and
 * the drawer's own header states that letter four inches above this dialog. A
 * sampled letter here would be the page contradicting the record in view, and
 * would hand an adviser the way to talk to an S about a candidate the app knows
 * is an I.
 *
 * 現時月入 is on that list for exactly the same reason, and was wrongly sampled
 * until 2026-09-03. It is not a slice 3 field — the factsheet has carried
 * `monthlyIncome` all along, the 招募 panel BEHIND this dialog prints it
 * (`recruitPicks`), and the adviser opens this in front of the person whose
 * salary it is. A fixed 32,000 for every candidate contradicted the panel four
 * inches away and put a number in the adviser's mouth that the candidate could
 * correct on the spot. 期望收入 stays sampled because that one genuinely has
 * nowhere to come from yet.
 *
 * When slice 3 lands, two things change and nothing else:
 *   1. `DEFAULTS` is deleted and the values come from `factsheet.recruitment.*`
 *      — note `factValueOf` must move to `_.get` first, it cannot resolve a
 *      dot path today (§7).
 *   2. The 😣 / 🤔 / 💪 sections gain their empty states, which the mock draws.
 * ────────────────────────────────────────────────────────────────────────────
 */

/** Fixed placeholder payload — this is not client data. */
const DEFAULTS = {
  yearsOfExp: 8,
  targetIncome: 60000,
  /** What 活動量計算 would answer for the income gap above. */
  meetingsPerMonth: 30,
  /** The 招募筆記 the 🤖 section quotes. */
  noteDate: '2026-08-21'
};

const WHO_COPY = {
  'zh-Hant-HK': {
    years: '相關年資',
    referrer: '介紹人',
    real: '現時月入來自客戶資料，請在客戶資料中修改。',
    reset: '恢復預設值'
  },
  zh: {
    years: '相关年资',
    referrer: '介绍人',
    real: '当前月收入来自客户资料，请在客户资料中修改。',
    reset: '恢复默认值'
  },
  en: {
    years: 'Years of experience',
    referrer: 'Referred by',
    real: 'Current income comes from the client record. Edit it in client details.',
    reset: 'Restore default values'
  }
} as const;

type WhoDraft = Record<'occupation' | 'yearsOfExp' | 'network' | 'targetIncome' | 'referrer', string>;
const whoDraftOf = (who: RecruitIdeaWho): WhoDraft => ({
  occupation: who.occupation,
  yearsOfExp: who.yearsOfExp === null ? '' : String(who.yearsOfExp),
  network: who.network,
  targetIncome: who.targetIncome === null ? '' : String(who.targetIncome),
  referrer: who.referrer
});
const optionalNumber = (raw: string, max: number): number | null => {
  if (!raw.trim()) return null;
  const value = Number(raw);
  return Number.isFinite(value) ? Math.max(0, Math.min(max, value)) : null;
};

/**
 * 家人反對 is the one concern that is not the candidate's own — it needs a
 * second person in the room to resolve, which is why it is marked wherever it
 * appears (§6.2).
 */
const NEEDS_SOMEONE_ELSE = 'family';

export type RecruitIdeaDialogProps = {
  open: boolean;
  onClose: () => void;
  intl: IntlLike;
  /**
   * Whose 候選人評估 scores to show in 他做得來嗎 (useRecruitScores, so they
   * follow the drawer's radar).
   */
  clientId?: string | null;
  /** Shown in the title. Real, like the letter below — see `discLetter`. */
  candidateName: string;
  /**
   * The candidate's DISC letter, off the record.
   *
   * REAL, not sampled — the communication page reads it beside the real
   * birthday. DISC shipped in Phase 1 while §6.1 and §6.2 wait for slice 3. Faking it
   * would have shown an I-type candidate the way to talk to an S, next to a
   * header readout stating the real letter four inches above.
   *
   * Empty when there is no letter on file: the section says so rather than
   * picking one, the same answer the inline playbook gives.
   */
  discLetter?: string | null;
  /**
   * The candidate's 現時月入, off the record — already normalised by the drawer
   * (`recruitIncomeNumber`) so this dialog and the 招募 panel behind it are
   * reading ONE value and cannot print two different salaries for one person.
   *
   * REAL, not sampled — see the file header. Null when nothing is on file, and
   * the row says so with the same 「—」 the panel uses rather than inventing a
   * figure to fill the gap.
   */
  monthlyIncome?: number | null;
  /** Same birthday fact Smart IDEA's 性格類型 card reads. */
  dob?: string | null;
  /**
   * Opens 活動量計算 from the income chain. Optional: with no handler the chain
   * still states the arithmetic, it just stops being a door.
   */
  onOpenCalculator?: () => void;
  /** 建團隊收入模擬器, shown with the candidate. Without it the button is hidden. */
  onOpenIncomeSimulator?: () => void;
  /** Saves a DISC letter picked in 溝通方式 through the drawer's existing fact flow. */
  /** Returns false when the change was not allowed. */
  onChangePersonality?: (letter: 'D' | 'I' | 'S' | 'C') => boolean | void;
  /** Opens the existing birthday editor from the shared card's missing-date hint. */
  onSetBirthday?: () => void;
  /**
   * Saves a birthday picked in place from the same hint, as Smart IDEA does.
   * When given it replaces `onSetBirthday`; leave it out (e.g. no edit rights)
   * and the hint falls back to that handler.
   */
  onPickBirthday?: (value: string) => void;
  /** Opens the 職涯測驗 dialog to view or copy the quiz link/results. */
  onOpenQuiz?: () => void;
};

/**
 * One 現況 → 建議 row, the shape both lookup tables render as.
 *
 * `detailLabel`/`detail` add the 🔍 追問 / 💬 化解話術 layer: the row itself
 * stays one glanceable line, and the question or script hides behind a chip
 * so three picks don't become a wall of text. Expanded state is per row and
 * resets when the dialog remounts — it is a peek, not a preference.
 *
 * `detail` is a RENDER FUNCTION: it receives the expanded flag so the quote
 * can type itself out as the panel opens (⑧) instead of sitting there fully
 * formed — a line being "said" reads like speech, a static paragraph like homework.
 */
const IdeaPair = (props: {
  from: string;
  to: string;
  /** Always shown under the pair — the DISC delivery line. */
  tip?: React.ReactNode;
  flagged?: boolean;
  detailLabel?: string;
  detail?: (active: boolean) => React.ReactNode;
}) => {
  const [expanded, setExpanded] = useState(false);
  const pair = (
    <div className={`clientv2-idea-pair${props.flagged ? ' is-flagged' : ''}`} data-testid="recruit-idea-pair">
      <span className="clientv2-idea-from">
        {props.flagged && <TriangleAlert size={13} aria-hidden="true" />}
        {props.from}
      </span>
      <ChevronRight size={14} className="clientv2-idea-arrow" aria-hidden="true" />
      <span className="clientv2-idea-to">{props.to}</span>
    </div>
  );
  if (!props.detail && !props.tip) return pair;
  if (!props.detail) {
    return (
      <div className="clientv2-idea-pair-block">
        {pair}
        {props.tip}
      </div>
    );
  }
  return (
    <div className="clientv2-idea-pair-block">
      {pair}
      {props.tip}
      <button
        type="button"
        className="clientv2-idea-more-toggle"
        data-testid="recruit-idea-more"
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
      >
        {props.detailLabel}
        <ChevronRight size={11} aria-hidden="true" />
      </button>
      <div className={`clientv2-idea-detail${expanded ? ' is-open' : ''}`}>
        <div>{props.detail(expanded)}</div>
      </div>
    </div>
  );
};

/**
 * 追問先行 — the 痛點 card, inverted.
 *
 * It used to read 痛點 → 賣點 with the probe question tucked behind a toggle.
 * That is the wrong way round for the recruiter this card exists for: a
 * recruiter handed a sell point pitches it, and the candidate — who has already
 * looked up the income online — hears one more person selling. Handed a
 * question, he has to ask it, and the candidate who says the pain out loud has
 * sold himself.
 *
 * So the probe IS the card, and the 賣點 waits behind 「他答完之後」. Same two
 * strings from §6.1, opposite emphasis; nothing new is generated.
 *
 * Keeps `recruit-idea-pair` as its test id: to everything outside this file it
 * is still one 痛點 row, and the count of rows is what the tests assert.
 */
const IdeaProbe = (props: { pain: string; probe: string; sell: string; sellLabel: string }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="clientv2-idea-pair-block clientv2-idea-probe-block" data-testid="recruit-idea-pair">
      <span className="clientv2-idea-probe-pain">{props.pain}</span>
      <p className="clientv2-idea-probe-ask" data-testid="recruit-idea-probe">
        {props.probe}
      </p>
      <button
        type="button"
        className="clientv2-idea-more-toggle"
        data-testid="recruit-idea-more"
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
      >
        {props.sellLabel}
        <ChevronRight size={11} aria-hidden="true" />
      </button>
      <div className={`clientv2-idea-detail${expanded ? ' is-open' : ''}`}>
        <div>
          <p className="clientv2-idea-probe-sell" data-testid="recruit-idea-sell">
            <TypewriterText text={props.sell} active={expanded} speed={14} />
          </p>
        </div>
      </div>
    </div>
  );
};

/** Label over value, the Smart IDEA 個人概覽 grid cell — used by 👤. */
const IdeaField = (props: { label: string; children: React.ReactNode; wide?: boolean }) => (
  <div className={`clientv2-idea-field${props.wide ? ' is-wide' : ''}`}>
    <span className="clientv2-idea-field-label">{props.label}</span>
    <span className="clientv2-idea-field-value">{props.children}</span>
  </div>
);

// A card in the Smart IDEA dashboard shape: icon + title over a dashed rule.
// 招募 IDEA's sections are the shared IdeaCard, tagged for this page's tests.
const IdeaSection = (props: Omit<React.ComponentProps<typeof IdeaCard>, 'testId'>) => (
  <IdeaCard {...props} testId="recruit-idea-section" />
);

/**
 * `BottomSheetOrDialog` is JavaScript behind react-intl's `injectIntl`, whose
 * typings drop its props — named here for the ones this file passes, the same
 * way ClientDetailDrawer's FactsSurface does.
 */
type IdeaSurfaceProps = {
  open: boolean;
  onClose: () => void;
  className?: string;
  header?: React.ReactNode;
  content?: React.ReactNode;
  DialogProps?: Record<string, unknown>;
  DialogContentProps?: Record<string, unknown>;
};
const IdeaSurface = BottomSheetOrDialog as unknown as React.ComponentType<IdeaSurfaceProps>;

/**
 * 可以分享的亮點 — four things about the job the adviser can bring up when the
 * candidate's own words lead there (time, pay, tools, mentoring), ordered and
 * worded for the candidate's DISC type (discPoints). Talking
 * points for the adviser, not a page to show: they used to be the support grid
 * of the retired 候選人展示 view, and read as a sales pitch put in front of the
 * candidate.
 */
const TALKING_POINTS = [
  { key: 'time', icon: '⏰' },
  { key: 'reward', icon: '⚖️' },
  { key: 'system', icon: '🚀' },
  { key: 'mentor', icon: '🤝' }
] as const;

const RecruitIdeaDialog = (props: RecruitIdeaDialogProps) => {
  const {
    open,
    onClose,
    intl,
    clientId,
    candidateName,
    discLetter,
    monthlyIncome,
    dob,
    onOpenCalculator,
    onOpenIncomeSimulator
  } = props;
  const { onChangePersonality, onSetBirthday, onPickBirthday, onOpenQuiz } = props;
  const t = (id: string, values?: Record<string, unknown>) => intl.formatMessage({ id }, values);

  const scores = useRecruitScores(clientId);
  const profile = useRecruitProfile(clientId);
  const [page, setPage] = useState<'overview' | 'communication'>('overview');
  useEffect(() => {
    if (!open) {
      setPage('overview');
    }
  }, [open]);
  // 應屆畢業生 / 有經驗人士 is answered in 快速資料 (or by the candidate's own
  // quiz) and only read here — the hook keeps this page in step while both are
  // open.
  const path = useRecruitIdeaPath(clientId);
  const pathCopy = recruitIdeaPathCopy((intl as IntlLike & { locale?: string }).locale);
  const whoCopy =
    WHO_COPY[((intl as IntlLike & { locale?: string }).locale || 'en') as keyof typeof WHO_COPY] || WHO_COPY.en;
  const whoStore = useRecruitIdeaWho(clientId, path);
  const sampleWho: RecruitIdeaWho = {
    occupation: path === 'graduate' ? pathCopy.graduateOccupation : t('clientv2-recruit-idea-demo-occupation'),
    yearsOfExp: path === 'graduate' ? null : DEFAULTS.yearsOfExp,
    network: path === 'graduate' ? pathCopy.graduateNetwork : t('clientv2-recruit-idea-demo-network'),
    targetIncome: path === 'graduate' ? null : DEFAULTS.targetIncome,
    referrer: path === 'graduate' ? '' : t('clientv2-recruit-idea-demo-referrer')
  };
  const who = whoStore.value || sampleWho;
  const stageLabel = path ? pathCopy[path] : null;
  const approachId = recruitApproachId(discLetter, path);
  // One card edits at a time; closing the dialog leaves edit mode.
  const [editing, setEditing] = useState<RecruitProfileKind | 'who' | null>(null);
  // 「DISC 只作溝通參考」 sits behind the ⓘ in DISC 對話建議's head.
  const [discNoteOpen, setDiscNoteOpen] = useState(false);
  // The candidate's DISC is set in one place — the persona card on 候選人概覽 —
  // through the same 性格類型 dialog the adviser uses for their own style.
  const [typePickerOpen, setTypePickerOpen] = useState(false);
  const openTypePicker = onChangePersonality ? () => setTypePickerOpen(true) : undefined;
  const renderBirthdayHint = onPickBirthday
    ? () => <SetBirthdayPicker hintText={t('set-birthday-first')} onPick={onPickBirthday} />
    : undefined;
  const [whoDraft, setWhoDraft] = useState<WhoDraft>(() => whoDraftOf(sampleWho));
  useEffect(() => {
    if (!open) setEditing(null);
  }, [open]);
  useEffect(() => setEditing(null), [clientId, path]);
  const money = (amount: number) => amount.toLocaleString();

  // 編輯 / 完成 in a card's header — the same pill as the persona card's 編輯.
  // None without a client to write to.
  const editToggle = (kind: RecruitProfileKind | 'who') =>
    clientId ? (
      <IdeaEditButton
        testId="recruit-idea-edit"
        kind={kind}
        pressed={editing === kind}
        label={t(editing === kind ? 'clientv2-recruit-profile-done' : 'clientv2-recruit-profile-edit')}
        onClick={() => {
          if (kind !== 'who') {
            setEditing(editing === kind ? null : kind);
            return;
          }
          if (editing === 'who') {
            whoStore.save({
              occupation: whoDraft.occupation.trim(),
              yearsOfExp: optionalNumber(whoDraft.yearsOfExp, 80),
              network: whoDraft.network.trim(),
              targetIncome: optionalNumber(whoDraft.targetIncome, 1000000000),
              referrer: whoDraft.referrer.trim()
            });
            setEditing(null);
          } else {
            setWhoDraft(whoDraftOf(who));
            setEditing('who');
          }
        }}
      />
    ) : null;

  // A card's body: the chips while editing, its picks once there are some, and
  // an invitation to add them otherwise — never a sample standing in for them.
  const profileBody = (kind: RecruitProfileKind, filled: React.ReactNode) => {
    if (clientId && editing === kind) return <RecruitProfileChips clientId={clientId} kind={kind} intl={intl} />;
    if (profile[kind].length > 0) return filled;
    return (
      <button
        type="button"
        className="clientv2-idea-empty"
        data-testid="recruit-idea-empty"
        data-kind={kind}
        disabled={!clientId}
        onClick={() => setEditing(kind)}
      >
        <Plus size={13} aria-hidden="true" />
        {t(`clientv2-recruit-profile-empty-${kind}`)}
      </button>
    );
  };

  return (
    // Presented like Smart IDEA (SmartIdeaDialog): the same BottomSheetOrDialog,
    // so a phone gets a bottom sheet closed from its header and a wider screen a
    // centred dialog. Kept mounted rather than returning null while closed, so
    // the sheet can slide away instead of vanishing — both surfaces render
    // nothing once closed.
    //
    // A banner names the person, then two internal pages separate candidate
    // context from conversation guidance. Both read the same real DISC/DOB facts.
    <IdeaSurface
      open={open}
      onClose={onClose}
      className="clientv2-recruit-idea-modal"
      header={<span id="clientv2-recruit-idea-title">{t('clientv2-recruit-idea')}</span>}
      DialogProps={{
        fullWidth: true,
        maxWidth: 'md',
        'data-testid': 'drawer-recruit-idea',
        'aria-labelledby': 'clientv2-recruit-idea-title'
      }}
      DialogContentProps={{ className: 'clientv2-idea-body' }}
      content={
        <div className="clientv2-idea-board">
          <DISCDialogComponent
            open={typePickerOpen}
            contentTextType="candidate"
            personality={discLetter || undefined}
            onClose={() => setTypePickerOpen(false)}
            onSubmit={(letter: string | undefined) => {
              if (isDiscLetter(letter)) onChangePersonality?.(letter);
            }}
            // Above the 招募 IDEA bottom sheet on mobile.
            style={{ zIndex: 2000 }}
          />
          <div className="clientv2-idea-banner">
            <span className="clientv2-idea-banner-badge" aria-hidden="true">
              <Briefcase size={16} />
            </span>
            <h3 className="clientv2-idea-name">{candidateName}</h3>
            {/* 招募路線 as the one pill left in the banner, because that is the
            size it deserves: it is a fact about the candidate, not a mode the
            adviser switches this page into. Answered in 快速資料. The 潛在招募
            and DISC pills that used to sit beside it are gone — the first only
            named the page the adviser had just opened, and the letter is read
            properly on the 溝通方式 page. */}
            {stageLabel && (
              <span className="clientv2-idea-pill" data-testid="recruit-idea-stage-pill" data-stage={path}>
                {stageLabel}
              </span>
            )}
          </div>

          <>
            <div className="clientv2-idea-tabs-row">
              <IdeaTabs
                tabs={[
                  { id: 'overview', label: t('clientv2-recruit-idea-overview-tab') },
                  { id: 'communication', label: t('clientv2-recruit-idea-how') }
                ]}
                value={page}
                onChange={setPage}
                idPrefix="recruit-idea"
                ariaLabel={t('clientv2-recruit-idea')}
              />
            </div>

            {page === 'overview' ? (
              <div
                id="recruit-idea-panel-overview"
                className="clientv2-idea-panel-overview"
                role="tabpanel"
                aria-labelledby="recruit-idea-tab-overview"
              >
                {/* 職涯測驗 first, like Smart IDEA's 保障小測驗: an invitation to send it
                    until the candidate has played it, then the freshest thing known
                    about them, with one button to apply it. */}
                <RecruitQuizCard
                  clientId={clientId}
                  intl={intl}
                  discOnFile={discLetter}
                  pathLabel={quizPath => pathCopy[quizPath]}
                  onOpenQuiz={onOpenQuiz}
                  onChangePersonality={onChangePersonality}
                />

                {/* ③ 候選人角色卡 — the candidate as a game card. Two seconds of
              looking beats two minutes of reading for the I-type adviser this
              page is built for. */}
                <RecruitPersonaCard
                  discLetter={discLetter}
                  strengths={profile.strengths}
                  intl={intl}
                  onSetType={openTypePicker}
                  onAddStrengths={clientId ? () => setEditing('strengths') : undefined}
                  readings={
                    <DobReadings
                      intl={intl}
                      dob={dob}
                      onSetBirthday={onSetBirthday}
                      renderBirthdayHint={renderBirthdayHint}
                    />
                  }
                />

                <div className="clientv2-idea-columns">
                  <div className="clientv2-idea-column">
                    <IdeaSection
                      id="who"
                      icon={<User size={18} aria-hidden="true" />}
                      title={t('clientv2-recruit-idea-who')}
                      action={editToggle('who')}
                    >
                      {editing === 'who' ? (
                        <div className="clientv2-idea-who-editor" data-testid="recruit-idea-who-editor">
                          {(
                            [
                              ['occupation', t('Occupation')],
                              ['yearsOfExp', whoCopy.years],
                              ['network', t('clientv2-recruit-idea-network')],
                              ['targetIncome', t('clientv2-recruit-idea-target')],
                              ['referrer', whoCopy.referrer]
                            ] as [keyof WhoDraft, string][]
                          ).map(([key, label]) => (
                            <label key={key}>
                              <span>{label}</span>
                              <input
                                data-testid={`recruit-idea-who-${key}`}
                                type={key === 'yearsOfExp' || key === 'targetIncome' ? 'number' : 'text'}
                                min={key === 'yearsOfExp' || key === 'targetIncome' ? 0 : undefined}
                                max={key === 'yearsOfExp' ? 80 : key === 'targetIncome' ? 1000000000 : undefined}
                                value={whoDraft[key]}
                                onChange={event => setWhoDraft({ ...whoDraft, [key]: event.target.value })}
                              />
                            </label>
                          ))}
                          <div className="clientv2-idea-who-real">
                            <span>{t('clientv2-recruit-income')}</span>
                            <strong>
                              {typeof monthlyIncome === 'number' && Number.isFinite(monthlyIncome)
                                ? money(monthlyIncome)
                                : '—'}
                            </strong>
                            <small>{whoCopy.real}</small>
                          </div>
                          {whoStore.value && (
                            <button
                              type="button"
                              className="clientv2-idea-who-reset"
                              onClick={() => {
                                whoStore.save(null);
                                setEditing(null);
                              }}
                            >
                              {whoCopy.reset}
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="clientv2-idea-grid">
                          <IdeaField label={t('Occupation')}>
                            {who.occupation || '—'}
                            {who.yearsOfExp !== null &&
                              ` · ${t('clientv2-recruit-idea-tenure', { years: who.yearsOfExp })}`}
                          </IdeaField>
                          <IdeaField label={t('clientv2-recruit-idea-network')}>{who.network || '—'}</IdeaField>
                          {/* The one pair on this page that mixes a real number with a
                      sampled one, deliberately: the salary is on file and the
                      target is not. Printed through the same `toLocaleString`
                      the panel uses, so 「18,000」 there is 「18,000」 here. */}
                          <IdeaField label={t('clientv2-recruit-income')}>
                            {typeof monthlyIncome === 'number' && Number.isFinite(monthlyIncome)
                              ? money(monthlyIncome)
                              : '—'}
                          </IdeaField>
                          <IdeaField label={t('clientv2-recruit-idea-target')}>
                            {who.targetIncome === null ? '—' : money(who.targetIncome)}
                          </IdeaField>
                          <IdeaField label={t('clientv2-recruit-path')} wide>
                            {who.referrer ? t('clientv2-recruit-referred-by', { name: who.referrer }) : '—'}
                          </IdeaField>
                        </div>
                      )}

                      {/* 現時月入 (on file) → 期望收入 (slice 3) → 活動量計算 (on file).
                    The chain is the strongest thing on this page: it turns 「收入
                    無上限」 from a slogan into a number the candidate can check
                    for themselves. Rendered as a button only when there is
                    somewhere to go — a dead CTA is worse than a plain sentence. */}
                      {who.targetIncome !== null && (
                        <div className="clientv2-idea-chain">
                          <Calculator size={15} aria-hidden="true" />
                          <span className="clientv2-idea-chain-copy">
                            {!whoStore.value && path !== 'graduate'
                              ? t('clientv2-recruit-idea-chain', {
                                  income: money(DEFAULTS.targetIncome),
                                  meetings: DEFAULTS.meetingsPerMonth
                                })
                              : `${t('clientv2-recruit-idea-target')}: ${money(who.targetIncome)}`}
                          </span>
                          {onOpenCalculator && (
                            <button
                              type="button"
                              className="clientv2-idea-chain-cta"
                              data-testid="recruit-idea-calculator"
                              onClick={onOpenCalculator}
                            >
                              {t('clientv2-recruit-idea-chain-cta')}
                              <ChevronRight size={13} aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      )}
                      {/* 建團隊收入模擬器: the same question — what could this pay? — as
                    a 5-year picture to go through together. Offered whether or
                    not a target income is on file. */}
                      {onOpenIncomeSimulator && (
                        <div className="clientv2-idea-chain">
                          <Wallet size={15} aria-hidden="true" />
                          <span className="clientv2-idea-chain-copy">{t('clientv2-recruit-idea-income-sim')}</span>
                          <button
                            type="button"
                            className="clientv2-idea-chain-cta"
                            data-testid="recruit-idea-income-sim"
                            onClick={onOpenIncomeSimulator}
                          >
                            {t('clientv2-recruit-income-sim')}
                            <ChevronRight size={13} aria-hidden="true" />
                          </button>
                        </div>
                      )}
                    </IdeaSection>

                    <IdeaSection
                      id="why"
                      icon={<Frown size={18} aria-hidden="true" />}
                      title={t('clientv2-recruit-idea-why')}
                      hint={t('clientv2-recruit-idea-why-hint')}
                      action={editToggle('painPoints')}
                    >
                      {profileBody(
                        'painPoints',
                        <div className="clientv2-idea-tags">
                          {profile.painPoints.map(key => (
                            <span className="clientv2-idea-tag" key={key} data-testid="recruit-idea-fact">
                              {t(`clientv2-recruit-pain-${key}`)}
                            </span>
                          ))}
                        </div>
                      )}
                    </IdeaSection>

                    <IdeaSection
                      id="fear"
                      icon={<CircleHelp size={18} aria-hidden="true" />}
                      title={t('clientv2-recruit-idea-fear')}
                      hint={t('clientv2-recruit-idea-fear-hint')}
                      action={editToggle('concerns')}
                    >
                      {profileBody(
                        'concerns',
                        <>
                          <div className="clientv2-idea-tags">
                            {profile.concerns.map(key => (
                              <span
                                className={`clientv2-idea-tag${key === NEEDS_SOMEONE_ELSE ? ' is-flagged' : ''}`}
                                key={key}
                                data-testid="recruit-idea-fact"
                              >
                                {key === NEEDS_SOMEONE_ELSE && <TriangleAlert size={12} aria-hidden="true" />}
                                {t(`clientv2-recruit-concern-${key}`)}
                              </span>
                            ))}
                          </div>
                          {profile.concerns.includes(NEEDS_SOMEONE_ELSE) && (
                            <p className="clientv2-idea-footnote">{t('clientv2-recruit-idea-fear-family')}</p>
                          )}
                        </>
                      )}
                    </IdeaSection>
                  </div>

                  <div className="clientv2-idea-column">
                    <IdeaSection
                      id="strength"
                      icon={<Dumbbell size={18} aria-hidden="true" />}
                      title={t('clientv2-recruit-idea-strength')}
                      action={editToggle('strengths')}
                    >
                      <RecruitScoreSummary scores={scores} intl={intl} clientId={clientId} />
                      {profileBody(
                        'strengths',
                        <div className="clientv2-idea-tags">
                          {profile.strengths.map(key => (
                            <span className="clientv2-idea-tag" key={key} data-testid="recruit-idea-strength">
                              {t(recruitProfileLabelId('strengths', key))}
                            </span>
                          ))}
                        </div>
                      )}
                    </IdeaSection>
                  </div>
                </div>

                {/* Keep the demo AI note, but below every candidate fact card so
              it cannot outrank information the recruiter actually recorded. */}
                <IdeaSection id="ai" icon={<Bot size={18} aria-hidden="true" />} title={t('clientv2-recruit-idea-ai')}>
                  <p className="clientv2-idea-quote">
                    {path === 'graduate' ? pathCopy.graduateAi : t('clientv2-recruit-idea-demo-ai')}
                  </p>
                  {path !== 'graduate' && (
                    <p className="clientv2-idea-source">
                      {t('clientv2-recruit-idea-ai-source', {
                        date: moment(DEFAULTS.noteDate).format('D MMM YYYY')
                      })}
                    </p>
                  )}
                </IdeaSection>
              </div>
            ) : (
              <div
                id="recruit-idea-panel-communication"
                className="clientv2-idea-panel-communication"
                role="tabpanel"
                aria-labelledby="recruit-idea-tab-communication"
              >
                {/* Recruiter's DISC vs Candidate's DISC chemistry & blind spot coaching */}
                <DiscChemistryCard mode="recruit" name={candidateName} disc={discLetter} intl={intl} />

                <IdeaSection
                  id="how"
                  icon={<Speech size={18} aria-hidden="true" />}
                  title={t('clientv2-recruit-idea-disc-guidance')}
                  action={
                    <>
                      <button
                        type="button"
                        className="clientv2-idea-info"
                        data-testid="recruit-idea-disc-note-toggle"
                        aria-expanded={discNoteOpen}
                        aria-label={t('clientv2-recruit-disc-reference')}
                        title={t('clientv2-recruit-disc-reference')}
                        onClick={() => setDiscNoteOpen(open => !open)}
                      >
                        <Info size={15} aria-hidden="true" />
                      </button>
                      {isDiscLetter(discLetter) && (
                        <span
                          className="clientv2-idea-type-chip"
                          data-testid="recruit-idea-type-chip"
                          style={{
                            color: COVER_ANIMALS[discLetter].color,
                            background: COVER_ANIMALS[discLetter].bg
                          }}
                        >
                          {discLabel(discLetter, intl.locale)}
                        </span>
                      )}
                    </>
                  }
                >
                  {discNoteOpen && (
                    <p className="clientv2-idea-disc-hint" data-testid="recruit-idea-disc-note">
                      {t('clientv2-recruit-disc-reference')}
                    </p>
                  )}
                  {isDiscLetter(discLetter) ? (
                    // 開場問題 is the opener below — no second copy here.
                    <DiscTips mode="recruit" letter={discLetter} intl={intl} testId="recruit-idea-disctips" />
                  ) : (
                    <div className="clientv2-idea-disc-empty" data-testid="recruit-idea-disc-empty">
                      <p className="clientv2-idea-footnote">{t('clientv2-recruit-disc-empty')}</p>
                      {openTypePicker && (
                        <button
                          type="button"
                          className="clientv2-persona-set"
                          data-testid="recruit-idea-disc-set-type"
                          onClick={openTypePicker}
                        >
                          {t('choose-personality')}
                        </button>
                      )}
                    </div>
                  )}
                  {/* The line to say comes last, once the adviser has read who they are
                      talking to; the DISC × route approach follows it once the route
                      is on file. */}
                  <div className="clientv2-idea-approach" data-testid="recruit-idea-approach" data-stage={path || ''}>
                    <RecruitOpener
                      title={t('clientv2-recruit-approach')}
                      candidateName={candidateName}
                      discLetter={discLetter}
                      painPoints={profile.painPoints}
                      intl={intl}
                    />
                    {approachId && (
                      <div className="clientv2-idea-approach-strategy" data-testid="recruit-idea-approach-strategy">
                        <p className="clientv2-idea-approach-line">{t(approachId)}</p>
                        <p className="clientv2-idea-approach-from">
                          {t('clientv2-recruit-approach-from', { disc: discLetter, stage: stageLabel })}
                        </p>
                      </div>
                    )}
                  </div>
                </IdeaSection>

                {/* 會談流程 — the meeting top to bottom: 開場 → 探索 → 化解 → 約定, each
                    step holding all of its own content. It replaces a storyline
                    that previewed only the first line of each card plus the two
                    cards themselves; the 探索 / 化解 picks are edited right here. */}
                <IdeaSection
                  id="meeting-flow"
                  icon={<Route size={18} aria-hidden="true" />}
                  title={t('clientv2-recruit-flow')}
                  hint={t('clientv2-recruit-flow-hint')}
                >
                  <IdeaMeetingFlow
                    minutesLabel={n => t('clientv2-recruit-flow-minutes', { n })}
                    steps={[
                      {
                        id: 'open',
                        marker: 1,
                        title: t('clientv2-recruit-flow-open'),
                        minutes: 5,
                        note: t('clientv2-recruit-flow-open-note'),
                        close: false
                      },
                      {
                        id: 'explore',
                        marker: 2,
                        title: `${t('clientv2-recruit-flow-explore')} · ${t('clientv2-recruit-idea-pain-strategy')}`,
                        minutes: 10,
                        action: editToggle('painPoints'),
                        note: t('clientv2-recruit-idea-why-hint'),
                        content: profileBody(
                          'painPoints',
                          <>
                            {profile.painPoints.map(key => (
                              <IdeaProbe
                                key={key}
                                pain={t(`clientv2-recruit-pain-${key}`)}
                                probe={t(`clientv2-recruit-pain-${key}-probe`)}
                                sell={t(`clientv2-recruit-pain-${key}-sell`)}
                                sellLabel={t('clientv2-recruit-idea-sell-toggle')}
                              />
                            ))}
                          </>
                        )
                      },
                      {
                        id: 'resolve',
                        marker: 3,
                        title: `${t('clientv2-recruit-flow-resolve')} · ${t('clientv2-recruit-idea-concern-strategy')}`,
                        minutes: 10,
                        action: editToggle('concerns'),
                        note: t('clientv2-recruit-idea-fear-hint'),
                        content: profileBody(
                          'concerns',
                          <>
                            {profile.concerns.map(key => (
                              <IdeaPair
                                key={key}
                                flagged={key === NEEDS_SOMEONE_ELSE}
                                from={t(`clientv2-recruit-concern-${key}`)}
                                to={t(`clientv2-recruit-concern-${key}-answer`)}
                                tip={
                                  isDiscLetter(discLetter) && (
                                    <DiscLine
                                      letter={discLetter}
                                      prefix={t('clientv2-idea-disc-line', {
                                        type: discLabel(discLetter, intl.locale)
                                      })}
                                      text={t(`clientv2-recruit-concern-${key}-disc-${discLetter}`)}
                                      testId="recruit-concern-disc-line"
                                    />
                                  )
                                }
                                detailLabel={t('clientv2-recruit-idea-script-toggle')}
                                detail={active => (
                                  <>
                                    <p className="clientv2-idea-quote-bubble">
                                      <TypewriterText
                                        text={t(`clientv2-recruit-concern-${key}-script`)}
                                        active={active}
                                        speed={14}
                                      />
                                    </p>
                                    <p className="clientv2-idea-quote-bubble is-timing">
                                      {t('clientv2-recruit-idea-timing-label')}：
                                      {t(`clientv2-recruit-concern-${key}-timing`)}
                                    </p>
                                  </>
                                )}
                              />
                            ))}
                            {profile.concerns.includes(NEEDS_SOMEONE_ELSE) && (
                              <p className="clientv2-idea-footnote">{t('clientv2-recruit-idea-fear-family')}</p>
                            )}
                          </>
                        )
                      },
                      {
                        id: 'close',
                        marker: <Handshake size={14} />,
                        title: t('clientv2-recruit-flow-close'),
                        minutes: 5,
                        // How to close depends on who is across the table.
                        note: t(
                          isDiscLetter(discLetter)
                            ? `clientv2-recruit-flow-close-line-${discLetter}`
                            : 'clientv2-recruit-flow-close-line'
                        ),
                        close: true
                      }
                    ]}
                  />
                </IdeaSection>

                <IdeaSection
                  id="talking-points"
                  icon={<Lightbulb size={18} aria-hidden="true" />}
                  title={t('clientv2-recruit-talking-points')}
                >
                  <IdeaPoints
                    hint={
                      isDiscLetter(discLetter)
                        ? t('clientv2-idea-points-disc-hint', { type: discLabel(discLetter, intl.locale) })
                        : t('clientv2-recruit-talking-points-hint')
                    }
                    leadLabel={t('clientv2-idea-points-lead')}
                    points={discOrderedPoints(
                      TALKING_POINTS,
                      RECRUIT_POINT_ORDER,
                      discLetter,
                      key => `clientv2-recruit-talking-point-${key}-desc`
                    ).map(point => ({
                      key: point.key,
                      icon: point.icon,
                      title: t(`clientv2-recruit-talking-point-${point.key}`),
                      desc: t(point.descId),
                      lead: point.lead
                    }))}
                  />
                </IdeaSection>

                {/* ⑥ 吉祥物 — company in the corner, one tailored tip at a time. */}
                <RecruitMascot discLetter={discLetter} intl={intl} />
              </div>
            )}
          </>
        </div>
      }
      // Closing is the surface's own: the header ✕ on the sheet, 關閉 in the
      // dialog's actions — the same pair Smart IDEA gets.
    />
  );
};

export default RecruitIdeaDialog;
